import {ActionList, Link} from '@primer/react'
import {DesktopDownloadIcon, TerminalIcon} from '@primer/octicons-react'
import {CopilotAppOpenAction} from '@github-ui/copilot-app-launcher-shared/CopilotAppOpenAction'
import {CodeDropdownButton, type CodeDropdownButtonProps} from '@github-ui/code-dropdown-button'
import {CloneUrl} from '@github-ui/code-dropdown-button/components/LocalTab'
import type {Repository} from '@github-ui/current-repository'
import {useNavigate} from '@github-ui/use-navigate'
import {buildCodespacesPath} from '@github-ui/codespaces-tab/build-codespaces-path'
import {useCallback, type CSSProperties, type ReactNode} from 'react'
import {useIsOverviewEnabled} from '../hooks/use-feature-state'
import {usePullRequestAnalytics} from '../hooks/use-pull-request-analytics'

import styles from './PullRequestCodeButton.module.css'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  buildCopilotAppPullRequestOpenTarget,
  COPILOT_APP_OPEN_ACTION_FEATURE_FLAG,
} from '../utils/copilot-app-open-action'

const localActionIconViewportStyle = {
  width: 'var(--base-size-20)',
  height: 'var(--base-size-20)',
} satisfies CSSProperties

export interface PullRequestCodeButtonProps {
  codespacesEnabled: boolean
  headBranch: string
  isEnterprise: boolean
  pullRequestId: number
  pullRequestNumber: number
  repository: Repository
  size?: 'small' | 'medium'
  iconOnly?: boolean
  variant?: CodeDropdownButtonProps['variant']
  trailingVisual?: boolean
}

export function PullRequestCodeButton({
  codespacesEnabled,
  headBranch,
  isEnterprise,
  pullRequestId,
  pullRequestNumber,
  repository,
  size = 'small',
  iconOnly,
  variant,
  trailingVisual,
}: PullRequestCodeButtonProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const showUpdatedHeader = useIsOverviewEnabled()
  const codespacesPath = buildCodespacesPath(repository.id, headBranch, {
    currentBranchName: headBranch,
    pullRequestId,
  })

  const handleMenuOpen = useCallback(() => {
    sendPullRequestAnalyticsEvent('code_button.open', 'CODE_BUTTON')
  }, [sendPullRequestAnalyticsEvent])

  const handleTabSelect = useCallback(
    (tab: 'local' | 'codespaces') => {
      if (tab === 'local') {
        sendPullRequestAnalyticsEvent('code_button.local_tab_selected', 'CODE_BUTTON_LOCAL_TAB')
      } else if (tab === 'codespaces') {
        sendPullRequestAnalyticsEvent('code_button.codespaces_tab_selected', 'CODE_BUTTON_CODESPACES_TAB')
      }
    },
    [sendPullRequestAnalyticsEvent],
  )

  const handleLocalTabAction = useCallback(
    (action: 'copy_cli' | 'learn_more' | 'checkout_desktop') => {
      switch (action) {
        case 'copy_cli':
          sendPullRequestAnalyticsEvent('code_button.cli_command_copied', 'CODE_BUTTON_CLI_COPY')
          break
        case 'learn_more':
          sendPullRequestAnalyticsEvent('code_button.cli_learn_more_clicked', 'CODE_BUTTON_CLI_LEARN_MORE')
          break
        case 'checkout_desktop':
          sendPullRequestAnalyticsEvent('code_button.desktop_checkout_clicked', 'CODE_BUTTON_DESKTOP_CHECKOUT')
          break
      }
    },
    [sendPullRequestAnalyticsEvent],
  )

  return (
    <CodeDropdownButton
      size={size}
      className={showUpdatedHeader ? undefined : styles.hideLeadingVisual}
      isEnterprise={isEnterprise}
      showCodespacesTab={codespacesEnabled}
      codespacesPath={codespacesPath}
      refName={headBranch}
      currentBranchName={headBranch}
      createRef={headBranch}
      localTab={<LocalTab pullNumber={pullRequestNumber} repository={repository} onAction={handleLocalTabAction} />}
      iconOnly={iconOnly}
      variant={variant}
      trailingVisual={trailingVisual}
      onMenuOpen={handleMenuOpen}
      onTabSelect={handleTabSelect}
    />
  )
}

interface LocalTabProps {
  pullNumber: number
  repository: Repository
  onAction?: (action: 'copy_cli' | 'learn_more' | 'checkout_desktop') => void
}

function LocalActionIconViewport({children}: {children: ReactNode}) {
  return (
    <span className="d-inline-flex flex-items-center flex-justify-center" style={localActionIconViewportStyle}>
      {children}
    </span>
  )
}

function LocalTab(props: LocalTabProps) {
  const {pullNumber, repository, onAction} = props
  const promptText = 'Checkout with GitHub CLI'
  const cloneText = `gh pr checkout ${pullNumber}`
  const navigate = useNavigate()
  const showCopilotAppOpenAction = isFeatureEnabled(COPILOT_APP_OPEN_ACTION_FEATURE_FLAG)
  const copilotAppPullRequestLink = showCopilotAppOpenAction
    ? buildCopilotAppPullRequestOpenTarget({
        owner: repository.ownerLogin,
        repo: repository.name,
        number: pullNumber,
      })
    : null

  const handleDesktopCheckout = useCallback(() => {
    onAction?.('checkout_desktop')
    navigate('https://desktop.github.com')
  }, [navigate, onAction])

  return (
    <>
      <div className="mt-2 tmp-px-3 py-2">
        <div className="d-flex flex-items-center mb-2">
          <TerminalIcon className="mr-2" />
          <p className="text-bold mb-0">{promptText}</p>
        </div>
        <CloneUrl
          buttonAriaLabel="Copy command to clipboard"
          inputId="checkout-with-gh-cli"
          inputLabel={`${promptText} command`}
          url={cloneText}
          onCopy={() => onAction?.('copy_cli')}
        />
        <p className="text-normal color-fg-muted">
          Work fast with our official CLI.{' '}
          <Link
            inline
            href="https://cli.github.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Learn more about the GitHub CLI"
            onClick={() => onAction?.('learn_more')}
          >
            Learn more
          </Link>
        </p>
      </div>
      <ActionList variant="inset" className="border-top" aria-label="Local checkout options">
        {showCopilotAppOpenAction && (
          <CopilotAppOpenAction entryPoint="dotcom_pr_code_menu" open={copilotAppPullRequestLink} />
        )}
        <ActionList.Item onSelect={handleDesktopCheckout}>
          <ActionList.LeadingVisual>
            <LocalActionIconViewport>
              <DesktopDownloadIcon />
            </LocalActionIconViewport>
          </ActionList.LeadingVisual>
          Checkout with GitHub Desktop
        </ActionList.Item>
      </ActionList>
    </>
  )
}
